// @ts-nocheck
import { useRef, useLayoutEffect } from "react";

const isBrowser = typeof window !== `undefined`;

function getScrollPosition({ element, useWindow }) {
  if (!isBrowser) return { x: 0, y: 0 };

  if (useWindow) {
    const pos = { x: window.scrollX, y: window.scrollY };
    console.log('[useScrollPosition] Window scroll:', pos);
    return pos;
  }

  // For non-window scroll, get the scroll position of the element
  const target = element && element.current ? element.current : null;
  
  if (target) {
    // Specific element provided
    const pos = { x: target.scrollLeft || 0, y: target.scrollTop || 0 };
    console.log('[useScrollPosition] Specific element scroll:', {
      element: target.tagName || 'unknown',
      position: pos,
      scrollHeight: target.scrollHeight,
      clientHeight: target.clientHeight,
      hasVerticalScroll: target.scrollHeight > target.clientHeight
    });
    return pos;
  }
  
  // No specific element, check scrollingElement first (standard way)
  const scrollingElement = document.scrollingElement || document.documentElement;
  
  const pos = {
    x: scrollingElement.scrollLeft || 0,
    y: scrollingElement.scrollTop || 0
  };
  
  // Fallback checks
  const htmlScroll = {
    x: document.documentElement.scrollLeft || 0,
    y: document.documentElement.scrollTop || 0
  };
  const bodyScroll = {
    x: document.body.scrollLeft || 0,
    y: document.body.scrollTop || 0
  };
  
  console.log('[useScrollPosition] Root scroll detection:', {
    scrollingElement: scrollingElement.tagName,
    scrollingElementPos: pos,
    htmlScroll,
    bodyScroll,
    windowScroll: { x: window.scrollX, y: window.scrollY }
  });
  
  return pos;
}

export function useScrollPosition(effect, deps, element, useWindow, wait) {
  const position = useRef(getScrollPosition({ element, useWindow }));
  const scrollingDivRef = useRef(null);

  let throttleTimeout = null;

  const callBack = (eventTarget = null) => {
    // If we captured a scrolling div and useWindow is true, use the div's scroll position
    let currPos;
    if (useWindow && scrollingDivRef.current && eventTarget && eventTarget !== window) {
      currPos = {
        x: eventTarget.scrollLeft || 0,
        y: eventTarget.scrollTop || 0
      };
      console.log('[useScrollPosition] Using DIV scroll instead of window:', {
        div: eventTarget.tagName,
        className: eventTarget.className,
        position: currPos
      });
    } else {
      currPos = getScrollPosition({ element, useWindow });
    }
    
    console.log('[useScrollPosition] Callback triggered:', {
      prevPos: position.current,
      currPos,
      changed: position.current.x !== currPos.x || position.current.y !== currPos.y
    });
    effect({ prevPos: position.current, currPos });
    position.current = currPos;
    throttleTimeout = null;
  };

  useLayoutEffect(() => {
    console.log('[useScrollPosition] Setting up scroll listener:', {
      useWindow,
      hasElement: !!(element && element.current),
      elementTag: element?.current?.tagName || 'none'
    });

    const handleScroll = (e) => {
      const scrollTarget = e.target;
      console.log('[useScrollPosition] Scroll event triggered from:', scrollTarget === window ? 'window' : scrollTarget.tagName, {
        scrollTop: scrollTarget.scrollTop,
        scrollLeft: scrollTarget.scrollLeft,
        className: scrollTarget.className || 'no-class',
        id: scrollTarget.id || 'no-id'
      });
      
      // Capture the scrolling div if we're in useWindow mode but getting div scroll events
      if (useWindow && scrollTarget !== window && scrollTarget.tagName === 'DIV' && !scrollingDivRef.current) {
        scrollingDivRef.current = scrollTarget;
        console.log('[useScrollPosition] Captured scrolling DIV for window mode:', {
          className: scrollTarget.className,
          id: scrollTarget.id
        });
      }
      
      if (wait) {
        if (throttleTimeout === null) {
          throttleTimeout = setTimeout(() => callBack(scrollTarget), wait);
        }
      } else {
        callBack(scrollTarget);
      }
    };

    // Determine which element to listen to
    let target;
    if (useWindow) {
      target = window;
    } else if (element && element.current) {
      target = element.current;
    } else {
      // Use scrollingElement (standard way to get the scrolling element)
      target = document.scrollingElement || document.documentElement;
    }

    console.log('[useScrollPosition] Listening to:', {
      isWindow: target === window,
      tagName: target === window ? 'window' : target.tagName,
      scrollHeight: target !== window ? target.scrollHeight : 'N/A',
      clientHeight: target !== window ? target.clientHeight : 'N/A'
    });

    // Add the scroll listener to the appropriate target
    target.addEventListener("scroll", handleScroll, { passive: true });

    // Also listen to window scroll if target is not window (for compatibility)
    if (target !== window) {
      console.log('[useScrollPosition] Also adding window scroll listener for compatibility');
      window.addEventListener("scroll", handleScroll, { passive: true });
    }

    // Also try body scroll if target is not body
    if (target !== document.body && target !== window) {
      console.log('[useScrollPosition] Also adding body scroll listener');
      document.body.addEventListener("scroll", handleScroll, { passive: true });
    }

    // Add a global capture listener on document as ultimate fallback
    console.log('[useScrollPosition] Adding document capture listener as fallback');
    document.addEventListener("scroll", handleScroll, { passive: true, capture: true });

    // Check all possible scroll containers
    console.log('[useScrollPosition] Scroll container info:', {
      html: {
        scrollHeight: document.documentElement.scrollHeight,
        clientHeight: document.documentElement.clientHeight,
        scrollTop: document.documentElement.scrollTop,
        hasScroll: document.documentElement.scrollHeight > document.documentElement.clientHeight
      },
      body: {
        scrollHeight: document.body.scrollHeight,
        clientHeight: document.body.clientHeight,
        scrollTop: document.body.scrollTop,
        hasScroll: document.body.scrollHeight > document.body.clientHeight
      },
      window: {
        scrollY: window.scrollY,
        innerHeight: window.innerHeight
      }
    });

    return () => {
      target.removeEventListener("scroll", handleScroll);
      if (target !== window) {
        window.removeEventListener("scroll", handleScroll);
      }
      if (target !== document.body && target !== window) {
        document.body.removeEventListener("scroll", handleScroll);
      }
      document.removeEventListener("scroll", handleScroll, { capture: true });
    };
  }, deps);
}
